import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Space } from 'antd';
import { t } from 'helpers/i18n';
import './BuildAppPopup.scss';
import AppInputSelector from '../input/AppInputSelector';
import AppInputText from '../input/AppInputText';
import AppMultiEmailsInput from '../input/AppMultiEmailsInput';
import { Result } from 'util/result';
import { RootState } from 'store/reducer';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppConfig } from 'data/terra/AppConfig';
import { Environment } from 'data/terra/Environment';
import { hideLoading, showLoading } from 'store/loading/actions';
import TerraBff from 'services/apis/terraBff/TerraBff';
import { BuildFlavor } from 'data/terra/BuildFlavor';
import { runAsync } from 'util/async';
import { BuildAppVersionResponse } from 'data/terra/api/terra/response/BuildAppVersionResponse';
import { BuildAppVersionRequest } from 'data/terra/api/terra/request/BuildAppVersionRequest';
import { userServices } from 'services';
import { asAxiosError } from 'util/resultExt';
import BranchName from "../../../components/shared/TooltipTitle";

export interface BuildAppPopupProps {
  handleCloseModal: () => void;
  onFinishWithResult: (result: Result<BuildAppVersionResponse>) => void;
}

function BuildAppPopup(props: BuildAppPopupProps) {
  const dispatch = useDispatch();
  const { selectedApp, selectedAppVersion, activeEnv, isLoading } = useSelector(
    stateSelector,
    shallowEqual
  );
  const [flavors, setFlavors] = useState<BuildFlavor[]>([]);
  const [selectedFlavor, setSelectedFlavor] = useState<BuildFlavor>();
  const [releaseNote, setReleaseNote] = useState<string>('');
  const [buildBranch, setBuildBranch] = useState<string>('');
  const [emails, setEmails] = useState<string[]>([]);
  const [isEmailsValid, setIsEmailsValid] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isBranchNameValid, setIsBranchNameValid] = useState<boolean>(true);

  const fetchGitConfigAndBuildFlavors = async () => {
    const userInfo = userServices.getUserInfo();
    if (userInfo && userInfo.email) {
      setEmails([userInfo.email]);
    }

    if (selectedApp) {
      dispatch(showLoading());
      const result = await TerraBff.gitConfig.getGitConfigByAppId(
        selectedApp?.id
      );
      if (result.isSuccess()) {
        const gitConfig = result.get()?.data;
        if (gitConfig) {
          const flavorResult = await TerraBff.gitConfig.getBuildFlavorsInGitConfig(
            gitConfig?.gitConfigId
          );
          dispatch(hideLoading());
          if (flavorResult.isSuccess()) {
            const flavors = flavorResult.get()?.data?.flavors ?? [];
            setFlavors(flavors);
            setSelectedFlavor(flavors?.find(e => e.isSelected));

            if (flavors.length === 0) {
              setErrorMessage(
                t('build_and_release_build_app_popup_build_flavor_empty_error')
              );
            }
          } else {
            const axiosError = asAxiosError(flavorResult.error);
            if (axiosError?.response?.status === 404) {
              setErrorMessage(
                t('build_and_release_build_app_popup_git_config_empty_error')
              );
            } else {
              setErrorMessage(
                t('build_and_release_build_app_can_not_get_popup_git_config')
              );
            }
          }
        } else {
          dispatch(hideLoading());
          setErrorMessage(t('build_and_release_git_config_id_not_found'));
        }
      } else {
        dispatch(hideLoading());
        const axiosError = asAxiosError(result.error);
        if (axiosError?.response?.status === 404) {
          setErrorMessage(t('build_and_release_git_config_id_not_found'));
        } else {
          setErrorMessage(t('build_and_release_cannot_get_git_config_id'));
        }
      }
    }
  };

  useEffect(() => {
    runAsync(fetchGitConfigAndBuildFlavors);
  }, []);

  const onSelectFlavor = useCallback(
    (value: string) => {
      setSelectedFlavor(flavors.find(e => e.buildFlavorId === value));
    },
    [flavors]
  );

  const onReleaseNoteChanged = (value?: string) => {
    setReleaseNote(value ?? '');
  };

  const onBuildBranchChanged = (value?: string) => {
    setBuildBranch(value ?? '');
  };

  const onEmailsChanged = (value: string[]) => {
    setEmails(value);
  };
  const onEmailsValidationChanged = (value: boolean) => {
    setIsEmailsValid(value);
  };

  const onBranchNameValidationChanged = (status: boolean) => {
    setIsBranchNameValid(status);
  };

  const onStartBuild = useCallback(async () => {
    if (selectedAppVersion && selectedFlavor) {
      dispatch(showLoading());
      const request = {
        buildFlavorId: selectedFlavor.buildFlavorId,
      } as BuildAppVersionRequest;

      emails && (request.registeredEmails = emails.join(','));
      releaseNote && (request.note = releaseNote);
      buildBranch && (request.branchName = buildBranch);

      const result = await TerraBff.buildApp.buildAppVersion(
        selectedAppVersion?.id,
        request
      );
      dispatch(hideLoading());
      props.onFinishWithResult(result);
    }
  }, [
    props.onFinishWithResult,
    selectedFlavor,
    releaseNote,
    buildBranch,
    emails,
  ]);

  return (
    <Modal
      open={true}
      maskClosable={false}
      width={768}
      modalRender={() => (
        <div className="build-app-popup-container">
          {isLoading && <div className="build-app-popup-progress-bar" />}
          {errorMessage && (
            <>
              <p className="ds-typo-subtitle1 m-0 flex-1 mr-12">{t('Error')}</p>
              <p className="ds-typo-body1 m-0 mt-8">
                <div style={{ marginBottom: 24 }}>{errorMessage}</div>
              </p>
              <div className="d-flex justify-content-end mt-16">
                <Space direction="horizontal" size={16}>
                  <Button
                    className="alert-modal-action-button"
                    type="primary"
                    ghost
                    onClick={props.handleCloseModal}
                  >
                    {t('close')}
                  </Button>
                </Space>
              </div>
            </>
          )}
          {!isLoading && !errorMessage && (
            <>
              <p className="ds-typo-title2 m-0 flex-1 mr-12">
                {t('build_and_release_build_app_popup_title', {
                  appName: selectedApp?.product.name,
                  appPlatform: selectedApp?.platform.name,
                  versionCode: selectedAppVersion?.code,
                })}
              </p>
              <p className="ds-typo-subtitle1 m-0 mt-8">
                <div style={{ marginBottom: 24 }}>
                  {t('build_and_release_build_app_popup_active_env', {
                    env: activeEnv?.name,
                  })}
                </div>
              </p>
              <div className="build-app-popup-content">
                <AppInputSelector
                  containerClassName="build-app-popup-input-row"
                  label={t('build_and_release_build_app_popup_build_flavor')}
                  options={flavors.map((e: BuildFlavor) => ({
                    value: e.buildFlavorId,
                    label: e.buildFlavorName,
                  }))}
                  selectedValue={selectedFlavor?.buildFlavorName}
                  onChange={onSelectFlavor}
                />
                <AppMultiEmailsInput
                  containerClassName="build-app-popup-input-row"
                  label={t('build_and_release_build_app_popup_received_emails')}
                  emails={emails}
                  placeholder={t(
                    'build_and_release_build_app_popup_received_emails_placeholder'
                  )}
                  onChanged={onEmailsChanged}
                  onValidationChanged={onEmailsValidationChanged}
                />
                <AppInputText
                  containerClassName="build-app-popup-input-row"
                  label={t('build_and_release_build_app_popup_release_note')}
                  value={releaseNote}
                  placeholder={t(
                    'build_and_release_build_app_popup_release_note_placeholder'
                  )}
                  allowClear
                  onInputChanged={onReleaseNoteChanged}
                />
                <AppInputText
                  containerClassName="build-app-popup-input-row"
                  label={t('build_and_release_build_app_popup_build_branch')}
                  value={buildBranch}
                  placeholder={t(
                    'build_and_release_build_app_popup_build_branch_placeholder'
                  )}
                  isAllowToolTip={true}
                  toolTipTitle={BranchName}
                  regex={new RegExp(`^(master|(tasks|features|feature|hotfix|epics|epic|impr|task)(\\/[A-Z]+([-_][A-Z0-9]+)*){1,2})$`)}
                  alert={isBranchNameValid ? "" : t('branch_build_alert_text')}
                  onValidationChanged={onBranchNameValidationChanged}
                  allowClear
                  onInputChanged={onBuildBranchChanged}
                />
              </div>
              <div className="d-flex justify-content-end mt-16">
                <Space direction="horizontal" size={16}>
                  <Button
                    className="alert-modal-action-button"
                    type="primary"
                    ghost
                    onClick={props.handleCloseModal}
                  >
                    {t('close')}
                  </Button>
                  <Button
                    disabled={!isEmailsValid || !isBranchNameValid}
                    className="alert-modal-action-button"
                    type="primary"
                    onClick={onStartBuild}
                  >
                    {t('build_and_release_build_app_popup_action_build')}
                  </Button>
                </Space>
              </div>
            </>
          )}
        </div>
      )}
    />
  );
}

export default BuildAppPopup;

const stateSelector = (rootState: RootState) => {
  const projectDetail = rootState.projectDetail;
  const appConfigs = rootState.projectDetail.appConfigs.data;
  const environments = rootState.common.environments;
  const activeConfig = appConfigs?.find((c: AppConfig) => c.isActive);

  return {
    selectedApp: projectDetail.selectedApp,
    selectedAppVersion: projectDetail.selectedAppVersion,
    activeEnv: environments.find(
      (e: Environment) => e.id === activeConfig?.env.id
    ),
    isLoading: rootState.loading.visible,
  };
};
